import {
    And,
    ComparisonFilter,
    ComparisonOperator,
    Equal,
    In,
    isComparisonFilter,
    NestedLogicalFilter,
    QueryStringInput,
    SortDirection
} from '@hello-aria/query-string'
import {
    ComparisonFilterInput,
    FilterParamsInput,
    NestedLogicalFilterInput
} from '@hello-aria/query-string/dist/stringify'
import { InfiniteData } from '@tanstack/react-query'
import { endOfDay, startOfDay } from 'date-fns'
import { isArray, isEmpty, isNil, isString, omitBy } from 'lodash'

import { PartialRecord } from '@/shared/utils/typesUtils'
import axios, { CanceledError } from 'axios'
import { DEFAULT_PAGE_SIZE } from '../enums/constants'
import { QueryParams, QueryParamsKey, QueryParamsValue, SortingRule } from '../models/queryModels'
import { RequestResponse } from '../services/requestServices'
import { removeWhiteSpace } from './stringUtils'
import { HttpError } from './requestsUtils'

type UnwrapArray<T> = T extends readonly (infer U)[] ? U : T
type NonU<T> = Exclude<T, undefined | null>

export function flattenInfiniteData<T>(
    data: InfiniteData<RequestResponse<T> | undefined> | undefined
): Array<UnwrapArray<T>> | undefined {
    if (!data?.pages) return undefined

    const bodies = data.pages
        .filter((p): p is NonU<typeof p> => !isNil(p))
        .map((p) => p.body)
        .filter((b): b is NonU<typeof b> => !isNil(b)) as Array<T>

    if (Array.isArray(bodies[0])) {
        return (bodies as unknown as Array<Array<UnwrapArray<T>>>).flat()
    }
    return bodies as unknown as Array<UnwrapArray<T>>
}

export const getInfiniteDataTotalCount = <T>(
    data: InfiniteData<RequestResponse<T> | undefined> | undefined
): number => {
    return data?.pages?.[0]?.pagination?.totalCount ?? 0
}

export type MapSortByInput = {
    id: string
    desc?: boolean
}[]

type MapSortBy = {
    path: string
    direction: SortDirection
}[]

export const mapSortBy = (sortBy: MapSortByInput): MapSortBy => {
    const { id, desc } = sortBy[0]
    return [{ path: id, direction: desc ? 'desc' : 'asc' }]
}

export interface MapPaginationInput {
    after?: string
    before?: string
    first?: number
    last?: number
}

export interface MapPagination {
    after?: string
    before?: string
    first: number
    last?: number
}

export const mapPagination = (pagination: MapPaginationInput): MapPagination => {
    const { first, ...rest } = pagination

    return {
        first: first ?? DEFAULT_PAGE_SIZE,
        ...omitBy(rest, (value) => isNil(value) || isEmpty(value))
    }
}

type SearchInputKeys = 'number' | 'name' | 'date' | 'email' | 'exact'

export interface MapSearchInput {
    value: string | undefined
    path: PartialRecord<SearchInputKeys, string | string[]>
}

const SEARCH_LIST_SEPARATORS = /[\s,;]+/
// An explicit separator means the user clearly typed/pasted a list. A bare space
// is ambiguous (e.g. a multi-word debtor name), so it is handled more leniently.
const EXPLICIT_LIST_SEPARATOR = /[,;\r\n]/
// Up to this many space-separated tokens could be a multi-word name, so we keep
// the fuzzy search alongside the exact match. Beyond it, it is clearly a pasted
// list — matching exactly only, which also keeps the request URL small.
const AMBIGUOUS_SPACE_TOKEN_LIMIT = 3

// A single amount (e.g. "100 000" or "100 000,30") is only digits, spaces and at
// most one decimal comma — keep it on the regular `number` search path instead of
// splitting it into a list. Two or more commas is always a list.
const looksLikeAmount = (value: string): boolean =>
    (value.match(/,/g) || []).length <= 1 && /^[\d\s,]+$/.test(value.trim())

/**
 * Split a pasted search value into a deduped list of trimmed tokens.
 * Supports whitespace (incl. newlines), comma and semicolon separators so users
 * can paste a list straight from a spreadsheet column or a separated line.
 */
export const parseSearchList = (value: string): string[] => [
    ...new Set(
        value
            .split(SEARCH_LIST_SEPARATORS)
            .map((token) => token.trim())
            .filter((token) => !isEmpty(token))
    )
]

export const mapSearch = (searchValue: MapSearchInput | undefined): QueryStringInput['filters'] => {
    if (searchValue) {
        const { value, path } = searchValue
        if (isNil(value) || isEmpty(value) || isNil(path)) return undefined

        const filters = Object.entries(path).reduce(
            (acc, [key, searchType]) => {
                const typedKey = key as SearchInputKeys

                if (typedKey === 'email') {
                    const sanitizedValue = removeWhiteSpace(value)

                    acc?.push({
                        type: 'comparison',
                        path: 'email',
                        value: sanitizedValue ? '%' + sanitizedValue + '%' : '',
                        operator: 'like'
                    })
                }

                if (typedKey === 'number') {
                    const valueWithoutSpace = removeWhiteSpace(value)
                    const valueWithDotInsteadOfComma = valueWithoutSpace.replace(/,/g, '.')
                    const valueWithoutTrailingZero = Number(valueWithDotInsteadOfComma)

                    if (!Number.isNaN(valueWithoutTrailingZero)) {
                        acc?.push({
                            type: 'comparison',
                            path: searchType,
                            value: valueWithoutTrailingZero ? '%' + String(valueWithoutTrailingZero) + '%' : '',
                            operator: 'like'
                        })
                    }
                }

                if (typedKey === 'name') {
                    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    const regex = new RegExp(`.*${escapedValue}.*`, 'ig')

                    acc?.push({
                        type: 'comparison',
                        path: searchType,
                        value: String(regex),
                        operator: 'regex'
                    })
                }

                if (typedKey === 'date') {
                    const isValidDate = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)

                    if (isValidDate) {
                        const [day, month, year] = value.split('/')

                        const startDate = startOfDay(new Date(Number(year), Number(month) - 1, Number(day))).getTime()
                        const endDate = endOfDay(new Date(Number(year), Number(month) - 1, Number(day))).getTime()

                        if (!Number.isNaN(startDate) && !Number.isNaN(endDate)) {
                            acc?.push({
                                type: 'logical',
                                operator: 'and',
                                filters: [
                                    {
                                        type: 'comparison',
                                        path: 'billDate',
                                        value: startDate.toString(),
                                        operator: 'gte'
                                    },
                                    {
                                        type: 'comparison',
                                        path: 'billDate',
                                        value: endDate.toString(),
                                        operator: 'lte'
                                    }
                                ]
                            })
                        }
                    }
                }

                return acc
            },
            [] as (ComparisonFilterInput | NestedLogicalFilterInput)[]
        )

        if (!isNil(path.exact) && !looksLikeAmount(value)) {
            const list = parseSearchList(value)
            if (list.length > 1) {
                const exactPaths = isArray(path.exact) ? path.exact : [path.exact]
                const inFilters = exactPaths.map((exactPath) => In(exactPath, list))

                // An explicit separator, or more tokens than a name could have,
                // means an unambiguous list → match exactly only. A short
                // space-separated value could be a multi-word name → keep the fuzzy
                // search alongside the exact match.
                const isExactOnly = EXPLICIT_LIST_SEPARATOR.test(value) || list.length > AMBIGUOUS_SPACE_TOKEN_LIMIT

                return [
                    {
                        type: 'logical',
                        operator: 'or',
                        filters: isExactOnly ? inFilters : [...filters, ...inFilters]
                    }
                ]
            }
        }

        return [
            {
                type: 'logical',
                operator: 'or',
                filters
            }
        ]
    }
}

const resolveFilters = (
    search: MapSearchInput | undefined,
    filter: FilterParamsInput | undefined
): FilterParamsInput[] | undefined => {
    if (isNil(search)) {
        return isNil(filter) ? undefined : [filter]
    }

    const formattedSearch = mapSearch(search)

    if (isNil(formattedSearch)) {
        return isNil(filter) || isEmpty(filter) ? undefined : [filter]
    }

    if (isNil(filter)) {
        return formattedSearch
    }

    formattedSearch.push(filter)
    return formattedSearch
}

interface MapQuery {
    sortBy?: MapSortByInput
    pagination?: MapPaginationInput
    search?: MapSearchInput
    filter?: FilterParamsInput
    params?: any
}

export const mapQuery = (query: MapQuery): QueryStringInput => {
    const { sortBy, pagination, search, params, filter } = query

    return {
        sorts: !isNil(sortBy) && !isEmpty(sortBy) ? mapSortBy(sortBy) : undefined,
        pagination: !isNil(pagination) ? mapPagination(pagination) : undefined,
        filters: resolveFilters(search, filter),
        params
    }
}

export const getListByIds = <T extends Record<string, unknown>>(
    callback: (filter: ComparisonFilterInput[]) => Promise<RequestResponse<T[]> | undefined>,
    ids: (string | null | undefined)[] | undefined
): Promise<RequestResponse<T[]> | undefined> | undefined => {
    try {
        const sanitizedIds = ids?.filter((id): id is string => !isNil(id))
        if (isNil(sanitizedIds) || isEmpty(sanitizedIds)) return undefined

        const response = callback([In('id', sanitizedIds)])
        return response
    } catch (error) {
        return undefined
    }
}

type Payload = PartialRecord<QueryParamsKey, QueryParamsValue>

export const setQueryParams = (payload: Payload, parsedQuery: QueryParams) => {
    return Object.entries(payload).reduce((accumulator, param) => {
        const [key, value] = param as [QueryParamsKey, QueryParamsValue]

        if (isEmpty(value)) {
            delete accumulator[key]
        } else {
            accumulator = { ...accumulator, [key]: mapFilterValues(key, value, accumulator[key]) ?? value }
            return accumulator
        }
        return accumulator
    }, parsedQuery as QueryParams)
}

const mapFilterValues = (
    key: QueryParamsKey,
    newValue: QueryParamsValue,
    currentValue?: QueryParamsValue
): QueryParamsValue | undefined => {
    if (!key.includes('_filter') || !isArray(newValue)) return undefined
    if (!currentValue) return newValue

    const newFilters = newValue as ComparisonFilterInput[]
    const existingFilters = generateFilterMap(currentValue as ComparisonFilterInput[])

    newFilters.forEach((filter) => {
        updateOrAddFilter(existingFilters, filter)
        removeEmptyFilter(existingFilters, filter)
    })

    return Object.values(existingFilters)
}

const generateFilterMap = (filters: ComparisonFilterInput[]): Record<string, ComparisonFilterInput> => {
    return filters.reduce(
        (acc, filter) => ({
            ...acc,
            [generateFilterKey(filter)]: filter
        }),
        {}
    )
}

const generateFilterKey = (filter: ComparisonFilterInput): string => `${filter.path}_${filter.operator}`

const updateOrAddFilter = (filtersMap: Record<string, ComparisonFilterInput>, filter: ComparisonFilterInput) => {
    const key = generateFilterKey(filter)
    filtersMap[key] = filter
}

const removeEmptyFilter = (filtersMap: Record<string, ComparisonFilterInput>, filter: ComparisonFilterInput) => {
    if (isEmpty(filter.value)) {
        const key = generateFilterKey(filter)
        delete filtersMap[key]
    }
}

export const pickFilterByOperatorAndPath = (
    filters: ComparisonFilterInput[] | undefined,
    operator: ComparisonOperator,
    path: string
): ComparisonFilterInput | undefined => {
    return filters?.find((item) => item.operator === operator && item.path === path)
}

const mapQueryParamsValue = (queryParamKey: QueryParamsKey, value: QueryParamsValue) => {
    if (queryParamKey.includes('_sort') && isString(value)) {
        const [sort, namespace] = value.slice(0, -1).split('(')
        return [{ id: namespace, desc: sort === 'desc' }]
    }
    if (queryParamKey.includes('_filter') && isString(value)) {
        return JSON.parse(atob(value))
    }

    return value
}

export const mapRouterQueryParams = (query: QueryParams | undefined) => {
    return Object.entries(query || {}).reduce((acc, param) => {
        const [key, value] = param as [QueryParamsKey, QueryParamsValue]
        acc = { ...acc, [key]: mapQueryParamsValue(key, value) }
        return acc
    }, {} as QueryParams)
}

const mapStringifyQueryParamValue = (queryParamKey: QueryParamsKey, value: QueryParamsValue) => {
    if (queryParamKey.includes('_sort') && isArray(value)) {
        const typedValue = value as SortingRule<any>[]
        return `${typedValue[0].desc ? 'desc' : 'asc'}(${typedValue[0].id})`
    }
    if (queryParamKey.includes('_filter') && isArray(value)) {
        return btoa(JSON.stringify(value))
    }

    return value
}

export const stringifyQueryParams = (query: QueryParams | undefined): string | undefined => {
    if (isNil(query)) {
        return undefined
    }

    if (isEmpty(query)) {
        return ''
    }

    const sanitizedQuery = omitBy(query, (param) => isNil(param) || isEmpty(param))
    const formattedQuery = Object.entries(sanitizedQuery).reduce((acc, param, index, array) => {
        const [key, value] = param as [QueryParamsKey, QueryParamsValue]
        const operator = index === array.length - 1 ? '' : '&'

        acc += `${key}=${mapStringifyQueryParamValue(key, value)}`

        return `${acc}${operator}`
    }, '')

    return formattedQuery
}

export const getPageSize = (query: QueryStringInput | undefined): number => {
    if (query?.pagination?.type === 'forward' && query?.pagination?.first) {
        return query.pagination.first
    }

    if (query?.pagination?.type === 'backward' && query?.pagination?.last) {
        return query.pagination.last
    }

    return DEFAULT_PAGE_SIZE
}

export const resolveQueryFilters = (additionalFilters?: FilterParamsInput[]): NestedLogicalFilter => {
    const filters: ComparisonFilter[] = []

    if (additionalFilters) {
        const comparisonFilters = additionalFilters
            .filter((filter): filter is ComparisonFilterInput => filter.type === 'comparison')
            .map(
                (filter) =>
                    ({
                        type: filter.type,
                        path: filter.path,
                        operator: filter.operator,
                        value: filter.value
                    }) as ComparisonFilter
            )

        filters.push(...comparisonFilters)
    }

    return And(...filters) as NestedLogicalFilter
}

/**
 * Adds a company filter to the query object to ensure data is filtered properly based on the user's access level.
 *
 * For "godmode" users, since the endpoint is private, an `owner.id = companyId` filter is explicitly added
 * to the query to ensure data is scoped correctly. For regular users, the backend automatically scopes
 * the data based on the token, so no additional filter is required.
 *
 * @param companyId - The ID of the company to filter by. If the value is 'me', no filter is applied.
 * @param query - An optional query object that may already contain filters.
 * @param field - The field to filter on. This is used to check if the filter already exists.
 * @returns A new query object with the company filter applied, if necessary.
 */
export const handlePrivateEndpointCompanyFilter = (
    companyId: string,
    query?: QueryStringInput,
    field = 'owner.id'
): QueryStringInput => {
    const companyFilter = companyId === 'me' ? undefined : Equal(field, companyId)
    const filters = query?.filters || []

    if (companyFilter && !filters.some((filter) => isComparisonFilter(filter) && filter.path === field)) {
        filters.push(companyFilter)
    }

    return { ...query, filters }
}
export const isErrorWithCode = (error: unknown): error is { code: string; status?: number; message?: string } => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof (error as { code?: unknown }).code === 'string'
    )
}

export const handleHttpError = (context: string, error: unknown): never => {
    console.error(`[${context}] Handling error:`, error)

    if (error instanceof CanceledError && error.name === 'CanceledError') {
        throw error
    }

    if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? 500
        const data = error.response?.data

        throw new HttpError(
            status,
            typeof data?.message === 'string' ? data.message : 'Unexpected server error',
            typeof data?.code === 'string' ? data.code : 'AXIOS_ERROR'
        )
    }

    if (isErrorWithCode(error)) {
        throw new HttpError(
            error.status ?? 500,
            typeof error.message === 'string' ? error.message : 'Unknown error occurred',
            error.code
        )
    }

    throw new HttpError(500, error instanceof Error ? error.message : 'Unknown error occurred', 'UNKNOWN_ERROR')
}
